import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseURL, config, requestID } from "../../../common/utils";
import { IonContent, IonPage } from "@ionic/react";

const Vendor = () => {
  const theme = useTheme();
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState({
    table: false,
    submit: false,
    delete: false,
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    residentialAddress: "",
    email: "",
    phone: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVendor(null);
    setFormValues({
      firstName: "",
      lastName: "",
      residentialAddress: "",
      email: "",
      phone: "",
    });
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const payload = {
      requestID: requestID(),
      ...formValues,
      user_id: selectedVendor?.user_id,
    };
//console.log(payload)
    try {
      setLoading((prev) => ({ ...prev, submit: true }));
      setError(null);

      const endpoint = selectedVendor
        ? `${baseURL}vendors/editVendor`
        : `${baseURL}vendors/addVendor`;
      const res = await axios.post(endpoint, payload, config);
//console.log(res)
       if (res.data.status === "true") {
        setSuccess(
          selectedVendor
            ? "Vendor updated successfully!"
            : "Vendor added successfully!"
        );
        fetchVendors();
        handleCloseDialog();
      } else {
        setError(res.data.message || "Operation failed. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading((prev) => ({ ...prev, table: true }));
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseURL}vendors/getAllVendors`,
        payload,
        config
      );
     // console.log(res)
      setVendors(res.data.data || []);
    } catch (error) {
      setError("Failed to fetch vendors. Please try again.");
      console.error("Error fetching vendors", error);
    } finally {
      setLoading((prev) => ({ ...prev, table: false }));
    }
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setFormValues({
      firstName: vendor.firstName,
      lastName: vendor.lastName,
      residentialAddress: vendor.residentialAddress,
      email: vendor.email,
      phone: vendor.phone,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      const payload = {
        requestID: requestID(),
        storeId: vendorId,
      };
      await axios.post(
        `${baseURL}vendor/deleteVendorById`,
        { data: payload, ...config }
      );
      console.log()
      setSuccess("Vendor deleted successfully!");
      fetchVendors();
    } catch (error) {
      setError("Failed to delete vendor. Please try again.");
      console.error("Failed to delete Vendor.", error);
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  const filteredVendors = vendors.filter((vendor) =>
    `${vendor.firstName} ${vendor.lastName} ${vendor.email} ${vendor.phone}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const paginatedVendors = filteredVendors.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <IonPage>
      <IonContent>


    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Vendor Management
        </Typography>
        <Button
          onClick={handleOpenDialog}
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ height: "fit-content" }}
        >
          Add Vendor
        </Button>
      </Box>

      {/* Success/Error Alerts */}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setSuccess(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setError(null)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Search Box */}
      <TextField
        fullWidth
        label="Search Vendors"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />,
        }}
        sx={{ mb: 3 }}
      />

      {/* Vendor Table */}
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableRow>
                {[
                  "First Name",
                  "Last Name",
                  "Location",
                  "Email",
                  "Phone",
                  "Actions",
                ].map((head) => (
                  <TableCell key={head}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {head}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading.table ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedVendors.length > 0 ? (
                paginatedVendors.map((vendor) => (
                  <TableRow key={vendor.id} hover>
                    <TableCell>{vendor.firstName}</TableCell>
                    <TableCell>{vendor.lastName}</TableCell>
                    <TableCell>{vendor.residentialAddress}</TableCell>
                    <TableCell>{vendor.email}</TableCell>
                    <TableCell>{vendor.phone}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(vendor)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(vendor.id)}
                            disabled={loading.delete}
                          >
                            {loading.delete ? (
                              <CircularProgress size={24} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1">
                      {searchTerm
                        ? "No matching vendors found"
                        : "No vendors available"}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      {filteredVendors.length > 0 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredVendors.length / rowsPerPage)}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedVendor ? "Edit Vendor" : "Add New Vendor"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formValues.firstName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formValues.lastName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Address"
              name="residentialAddress"
              value={formValues.residentialAddress}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formValues.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formValues.phone}
              onChange={handleInputChange}
              margin="normal"
              required
            />

              <TextField
              fullWidth
              label="password"
              name="password"
              value={formValues.password}
              onChange={handleInputChange}
              margin="normal"
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={
              loading.submit ||
              !formValues.firstName ||
              !formValues.lastName ||
              !formValues.residentialAddress ||
              !formValues.email ||
              !formValues.phone
            }
            startIcon={
              loading.submit ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {selectedVendor ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </IonContent>

    </IonPage>

  );
};

export default Vendor;